import { reaction, when } from 'mobx';

const TIMER_KEY = 'clip_editor_usable_creator';

// Late-starting loads (e.g. the transcript refetching after the editor mounts)
// can briefly un-ready the editor after it first looks usable.
const USABLE_QUIET_MS = 1000;
const NEVER_USABLE_TIMEOUT_MS = 60_000;

/** The AnalyticsStore timer API this tracker drives. */
export interface ClipEditorUsableAnalytics {
  startTimer(key: string, metadata?: Record<string, unknown>): void;
  markTimer(key: string, markName: string): void;
  endTimer(
    key: string,
    metadata?: Record<string, unknown>,
    endedAtMs?: number,
  ): void;
  reportTimerStillRunning(
    key: string,
    metadata?: Record<string, unknown>,
  ): void;
  cancelTimer(key: string, reason?: string): void;
}

/**
 * Narrow getter interface so the tracker stays decoupled from
 * VideoCreatorStore. The readiness getters must read observables.
 */
export interface ClipEditorUsableHost {
  getAnalytics(): ClipEditorUsableAnalytics;
  /** Renderer ready and assets loaded (isLoading / isLoadingAssets cleared). */
  isVideoReady(): boolean;
  isTranscriptReady(): boolean;
  getStoryId(): string | undefined;
  getStoryTitle(): string | undefined;
  getVideoId(): string | undefined;
  getClipTitle(): string | undefined;
}

/**
 * PLAT-11041: clip-editor time-to-usable in Creator Studio.
 * Starts at clip open (loadVideo / createNewVideoFromSource) and ends when
 * the editor is actually editable — video ready and the transcript loaded.
 * Emits clip_editor_usable_creator with per-gate marks so slow loads
 * self-explain (renderer vs transcript). A newer clip open supersedes a
 * pending timer.
 */
export class ClipEditorUsableTracker {
  private disposers: Array<() => void> = [];

  // PLAT-11680: only Creator Studio's Stage connects the renderer that ends
  // this timer; clip loads from other routes would always time out.
  private isCreatorStudioMounted = false;

  // Set by startEarly(); the next begin() adopts that timer instead.
  private isStartedEarly = false;

  // The running timer's clip. An adopting begin() replaces the early (URL)
  // id: the init may load a different clip than the URL names.
  private timedVideoId?: string;

  constructor(private host: ClipEditorUsableHost) {}

  setCreatorStudioMounted(mounted: boolean): void {
    this.isCreatorStudioMounted = mounted;
    if (!mounted) {
      this.isStartedEarly = false;
      if (this.shouldDispose()) {
        this.dispose();
        this.host.getAnalytics().cancelTimer(TIMER_KEY, 'left_creator_studio');
      }
    }
  }

  /**
   * Start ahead of the load that begin() would time, e.g. before the story
   * fetch on a cold boot. storyId: the story being opened (from the URL).
   */
  startEarly(videoId?: string, storyId?: string): void {
    if (!this.isCreatorStudioMounted) {
      return;
    }
    this.start(videoId, storyId);
    this.isStartedEarly = true;
  }

  /** Ends the window in which a load may adopt the early start. */
  clearEarlyStart(): void {
    this.isStartedEarly = false;
  }

  begin(videoId?: string): void {
    if (!this.isCreatorStudioMounted) {
      return;
    }
    if (this.isStartedEarly) {
      this.isStartedEarly = false;
      this.timedVideoId = videoId;
      return;
    }
    this.start(videoId);
  }

  private start(videoId?: string, storyId?: string): void {
    const { host } = this;
    const analytics = host.getAnalytics();
    this.dispose();
    this.timedVideoId = videoId;

    // Cold: the story isn't loaded yet (the loaded one, if any, is the
    // previous story), so the duration includes the story fetch.
    const isCold = !!storyId && storyId !== host.getStoryId();
    analytics.startTimer(TIMER_KEY, {
      surface: 'creator_studio',
      load_path: isCold ? 'cold' : 'warm',
      videoId,
      storyId: storyId ?? host.getStoryId(),
      storyTitle: isCold ? undefined : host.getStoryTitle(),
    });

    const editorUsable = () => host.isVideoReady() && host.isTranscriptReady();

    // Read at end: on a cold load / story switch the start runs before the
    // story (and clip) are loaded. Known start ids win.
    const currentIds = () => ({
      videoId: this.timedVideoId ?? host.getVideoId(),
      storyId: storyId ?? host.getStoryId(),
      clipTitle: host.getClipTitle(),
      storyTitle: host.getStoryTitle(),
    });

    this.disposers.push(
      when(
        () => host.isVideoReady(),
        () => analytics.markTimer(TIMER_KEY, 'video_ready_ms'),
      ),
      when(
        () => host.isTranscriptReady(),
        () => analytics.markTimer(TIMER_KEY, 'transcript_ready_ms'),
      ),
    );

    // Declare usable only once the predicate has held for a full quiet
    // window, and report the duration as of the moment it became usable —
    // the quiet window is confirmation, not user-perceived load time.
    let quietTimer: ReturnType<typeof setTimeout> | null = null;
    // Arm only after a genuine loading cycle: createNewVideoFromSource
    // starts this timer without setting isLoading, so the PREVIOUS clip's
    // fully-usable state would otherwise complete the new timer at ~1s
    // (false fast number). If the editor is already "usable" at start, wait
    // until it first becomes not-usable before a completion may count.
    let armed = !editorUsable();
    const disposeUsableReaction = reaction(
      editorUsable,
      usable => {
        if (quietTimer) {
          clearTimeout(quietTimer);
          quietTimer = null;
        }
        if (!usable) {
          armed = true;
          return;
        }
        if (usable && armed) {
          const usableAt = performance.now();
          quietTimer = setTimeout(() => {
            analytics.endTimer(TIMER_KEY, currentIds(), usableAt);
            this.dispose();
          }, USABLE_QUIET_MS);
        }
      },
      { fireImmediately: true },
    );
    this.disposers.push(() => {
      disposeUsableReaction();
      if (quietTimer) {
        clearTimeout(quietTimer);
        quietTimer = null;
      }
    });

    // Never-usable beacon: if the editor still isn't usable after 60s,
    // say so explicitly (black video / failed transcript sessions would
    // otherwise be silent). The timer keeps running — a late completion
    // still reports its true duration.
    const timeoutBeacon = setTimeout(() => {
      if (!armed) {
        // No loading cycle was ever observed (e.g. createNewVideoFromSource
        // over an already-usable editor): there is nothing to measure.
        // Retire the timer with a distinct reason instead of a false
        // "not usable after 60s" beacon.
        analytics.cancelTimer(TIMER_KEY, 'no_load_cycle_observed');
        return;
      }
      analytics.reportTimerStillRunning(TIMER_KEY, currentIds());
    }, NEVER_USABLE_TIMEOUT_MS);
    this.disposers.push(() => clearTimeout(timeoutBeacon));
  }

  // Disposers are live only between start() and completion.
  private shouldDispose(): boolean {
    return this.disposers.length > 0;
  }

  private dispose(): void {
    this.disposers.forEach(dispose => dispose());
    this.disposers = [];
  }
}
